import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, withKeys as _withKeys, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "adder-row mt-3" }
const _hoisted_2 = { class: "formula-preview" }
const _hoisted_3 = {
  key: 0,
  class: "preview-warning"
}
const _hoisted_4 = { class: "preview-price" }
const _hoisted_5 = { class: "preview-length" }
const _hoisted_6 = {
  key: 2,
  class: "preview-hint"
}

import { computed, ref, watch } from 'vue';
import AppDialog from '@/components/AppDialog.vue';
import { convertLength } from '@/utils/priceListUtils';
import type { SaleLineDetailsForDataTable } from '@/utils/helpers';
import type { UpdateSaleLineReqBody } from '@/services/pricingHistories.service';

type Mode = 'static' | 'formula';


export default /*@__PURE__*/_defineComponent({
  __name: 'SuggestedPriceEditor',
  props: {
    modelValue: { type: Boolean },
    item: {},
    loading: { type: Boolean, default: false }
  },
  emits: ["update:modelValue", "save"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const mode = ref<Mode>('static');
const staticPrice = ref<string>('');
const basePrice = ref<string>('');
const adderPerMeter = ref<string>('');
const adderPerFeet = ref<string>('');

// The line's quoted length, expressed in its own unit (F/M/I/CM).
const lengthValue = computed<number | null>(() => {
  const raw = props.item?.quotedProductLength;
  return raw === null || raw === undefined ? null : Number(raw);
});

const lineUnit = computed<string>(() => {
  const unit = props.item?.quotedProductLengthUnits;
  return unit && ['F', 'M', 'I', 'CM'].includes(unit) ? unit : 'M';
});

const hasLength = computed(() => lengthValue.value !== null && !Number.isNaN(lengthValue.value));

const lengthInMeters = computed<number | null>(() => {
  if (!hasLength.value) return null;
  return convertLength(lengthValue.value as number, lineUnit.value, false, true);
});

// Canonical per-meter adder used for all math and saves. Each field shows a
// 2-decimal representation (same display-vs-raw split as the search result
// card). Typing in either field (re)defines the canonical value; the other
// field is live-updated from it so display rounding never drifts the price.
const perMeterAdder = ref<number | null>(null);

const round = (value: number, decimals = 6) => Number(value.toFixed(decimals));

// Accept "," as decimal while always storing/displaying with ".". Keep
// in-progress values like "3." or "-" intact — type="number" would strip them
// and make the live-converting sibling feel jumpy.
const sanitizeDecimalText = (raw: unknown): string => {
  const match = String(raw ?? '')
    .replace(/,/g, '.')
    .match(/^-?\d*\.?\d*/);
  return match ? match[0] : '';
};

const isIncompleteDecimal = (value: string): boolean =>
  value === '-' || value === '.' || value === '-.';

const parseDecimal = (value: string): number | null => {
  if (value === '' || isIncompleteDecimal(value)) return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
};

const isValidNumber = (value: string) => parseDecimal(value) !== null;

const displayPerMeter = (): string =>
  perMeterAdder.value === null ? '' : String(round(perMeterAdder.value, 2));

const displayPerFeet = (): string =>
  perMeterAdder.value === null
    ? ''
    : String(round(convertLength(perMeterAdder.value, 'F', false, true), 2));

const onStaticPriceInput = (raw: unknown) => {
  staticPrice.value = sanitizeDecimalText(raw);
};

const onBasePriceInput = (raw: unknown) => {
  basePrice.value = sanitizeDecimalText(raw);
};

const onAdderPerMeterInput = (raw: unknown) => {
  const value = sanitizeDecimalText(raw);
  adderPerMeter.value = value;

  if (value === '') {
    perMeterAdder.value = null;
    adderPerFeet.value = '';
    return;
  }
  // Keep "3." / "-" as typed; don't rewrite this field or clear the sibling yet.
  if (isIncompleteDecimal(value)) return;

  const n = parseDecimal(value);
  if (n === null) return;

  perMeterAdder.value = n;
  adderPerFeet.value = displayPerFeet();
};

const onAdderPerFeetInput = (raw: unknown) => {
  const value = sanitizeDecimalText(raw);
  adderPerFeet.value = value;

  if (value === '') {
    perMeterAdder.value = null;
    adderPerMeter.value = '';
    return;
  }
  if (isIncompleteDecimal(value)) return;

  const n = parseDecimal(value);
  if (n === null) return;

  perMeterAdder.value = convertLength(n, 'F', false, false);
  adderPerMeter.value = displayPerMeter();
};

const computedPrice = computed<number | null>(() => {
  const base = parseDecimal(basePrice.value);
  if (base === null || perMeterAdder.value === null || lengthInMeters.value === null) return null;
  return base + perMeterAdder.value * lengthInMeters.value;
});

// The price currently shown on the line (manual override or engine price).
const currentPrice = computed<number | null>(() => {
  const p = props.item?.pricingInfo?.price;
  return p === null || p === undefined ? null : Number(p);
});

// The price this dialog would apply, given the active mode and inputs.
const resultingPrice = computed<number | null>(() => {
  if (mode.value === 'static') {
    const n = parseDecimal(staticPrice.value);
    return n !== null ? round(n, 4) : null;
  }
  return computedPrice.value !== null ? round(computedPrice.value, 4) : null;
});

// Saving a no-op is pointless: require the resulting price to differ from the
// current one (compared at the precision we send).
const priceChanged = computed(() => {
  if (resultingPrice.value === null) return false;
  if (currentPrice.value === null) return true;
  return resultingPrice.value !== round(currentPrice.value, 4);
});

const canSave = computed(() => {
  if (props.loading) return false;
  // Validate the visible adder text too: incomplete values like "-" / "." leave
  // perMeterAdder on the previous number (so sibling conversion can stay put
  // while typing), and must not keep Save enabled against a stale canonical.
  const validInputs =
    mode.value === 'static'
      ? isValidNumber(staticPrice.value)
      : isValidNumber(basePrice.value) &&
        isValidNumber(adderPerMeter.value) &&
        isValidNumber(adderPerFeet.value) &&
        perMeterAdder.value !== null &&
        computedPrice.value !== null;
  return validInputs && priceChanged.value;
});

// Reset sends `suggestedPrice: null`, and the backend treats that as "clear and
// reprice" whatever the price was: it wipes the engine price, the provenance and
// the references alongside a manual override, then runs the engine again on the
// spot, in any ERP state (PRICE-RECALCULATION.md). So it is not a manual-only
// action — on an engine-priced line it is how you get a fresh price when the old
// one came from a reference or price list that has since moved. The only line
// with nothing to reset is one that has no price at all.
const canReset = computed(() => props.item?.pricingInfo?.price != null);

// Reference/price-list slots cleared on every manual price so the line cleanly
// becomes a manual override (matches the search-view apply flow).
const clearedAssociations = (): UpdateSaleLineReqBody => ({
  priceListId: null,
  referenceProduct1: null,
  referenceProduct1Length: null,
  referenceProduct1Price: null,
  referenceProduct2: null,
  referenceProduct2Length: null,
  referenceProduct2Price: null,
});

const save = () => {
  if (!canSave.value) return;

  if (mode.value === 'static') {
    emit('save', {
      ...clearedAssociations(),
      priceType: 'static',
      suggestedPrice: round(parseDecimal(staticPrice.value) as number, 4),
      basePrice: null,
      perMeterAdder: null,
    });
    return;
  }

  emit('save', {
    ...clearedAssociations(),
    priceType: 'formula',
    suggestedPrice: round(computedPrice.value as number, 4),
    basePrice: round(parseDecimal(basePrice.value) as number),
    perMeterAdder: round(perMeterAdder.value as number),
  });
};

const reset = () => {
  if (!canReset.value) return;
  emit('save', { suggestedPrice: null });
};

const cancel = () => emit('update:modelValue', false);

const onDialogToggle = (value: boolean) => {
  if (!value) emit('update:modelValue', false);
};

const formatPrice = (value: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

const formatLength = (value: number | null): string => {
  if (value === null) return '';
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
};

const unitLabel = (unit: string): string => {
  switch (unit) {
    case 'F':
      return 'ft';
    case 'M':
      return 'm';
    case 'I':
      return 'in';
    case 'CM':
      return 'cm';
    default:
      return unit;
  }
};

// Reset inputs each time the dialog opens so it always reflects the clicked row.
// If the line already carries a base + adder, open in formula mode prefilled
// from it; otherwise open in static mode prefilled with the current price.
watch(
  () => props.modelValue,
  (open) => {
    if (!open) return;

    const current = props.item?.pricingInfo?.price;
    staticPrice.value = current !== null && current !== undefined ? String(round(Number(current), 4)) : '';

    const base = props.item?.basePrice;
    const perM = props.item?.perMeterAdder;
    const hasFormula = base !== null && base !== undefined && perM !== null && perM !== undefined;

    if (hasFormula) {
      mode.value = 'formula';
      basePrice.value = String(round(Number(base), 4));
      perMeterAdder.value = Number(perM);
      adderPerMeter.value = displayPerMeter();
      adderPerFeet.value = displayPerFeet();
    } else {
      mode.value = 'static';
      basePrice.value = '';
      perMeterAdder.value = null;
      adderPerMeter.value = '';
      adderPerFeet.value = '';
    }
  },
);

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_btn_toggle = _resolveComponent("v-btn-toggle")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_spacer = _resolveComponent("v-spacer")!

  return (_openBlock(), _createBlock(AppDialog, {
    "model-value": _ctx.modelValue,
    title: "Set suggested price",
    "max-width": "420",
    "onUpdate:modelValue": onDialogToggle
  }, {
    actions: _withCtx(() => [
      _createVNode(_component_v_btn, {
        variant: "text",
        "prepend-icon": "mdi-refresh",
        disabled: _ctx.loading || !canReset.value,
        onClick: reset
      }, {
        default: _withCtx(() => _cache[4] || (_cache[4] = [
          _createTextVNode(" Reset ")
        ])),
        _: 1
      }, 8, ["disabled"]),
      _createVNode(_component_v_spacer),
      _createVNode(_component_v_btn, {
        variant: "outlined",
        color: "secondary",
        disabled: _ctx.loading,
        onClick: cancel
      }, {
        default: _withCtx(() => _cache[5] || (_cache[5] = [
          _createTextVNode("Cancel")
        ])),
        _: 1
      }, 8, ["disabled"]),
      _createVNode(_component_v_btn, {
        color: "primary",
        variant: "flat",
        loading: _ctx.loading,
        disabled: !canSave.value,
        onClick: save
      }, {
        default: _withCtx(() => _cache[6] || (_cache[6] = [
          _createTextVNode("Save")
        ])),
        _: 1
      }, 8, ["loading", "disabled"])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", null, [
        _createVNode(_component_v_btn_toggle, {
          modelValue: mode.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((mode).value = $event)),
          mandatory: "",
          density: "comfortable",
          color: "primary",
          variant: "outlined",
          divided: "",
          class: "mode-toggle"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_btn, {
              value: "static",
              size: "small"
            }, {
              default: _withCtx(() => _cache[1] || (_cache[1] = [
                _createTextVNode("Manual")
              ])),
              _: 1
            }),
            _createVNode(_component_v_btn, {
              value: "formula",
              size: "small"
            }, {
              default: _withCtx(() => _cache[2] || (_cache[2] = [
                _createTextVNode("Base + Adder")
              ])),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["modelValue"]),
        (mode.value === 'static')
          ? (_openBlock(), _createBlock(_component_v_text_field, {
              key: 0,
              "model-value": staticPrice.value,
              type: "text",
              inputmode: "decimal",
              label: "Price",
              prefix: "$",
              density: "compact",
              variant: "outlined",
              "hide-details": "",
              autofocus: "",
              class: "mt-3",
              "onUpdate:modelValue": onStaticPriceInput,
              onKeyup: _withKeys(save, ["enter"])
            }, null, 8, ["model-value"]))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _createVNode(_component_v_text_field, {
                "model-value": basePrice.value,
                type: "text",
                inputmode: "decimal",
                label: "Base",
                prefix: "$",
                density: "compact",
                variant: "outlined",
                "hide-details": "",
                autofocus: "",
                class: "mt-3",
                "onUpdate:modelValue": onBasePriceInput,
                onKeyup: _withKeys(save, ["enter"])
              }, null, 8, ["model-value"]),
              _createElementVNode("div", _hoisted_1, [
                _createVNode(_component_v_text_field, {
                  "model-value": adderPerMeter.value,
                  type: "text",
                  inputmode: "decimal",
                  label: "Adder per Meter",
                  prefix: "$",
                  suffix: "/M",
                  "suffix-class": 'text-grey-darken-1',
                  density: "compact",
                  variant: "outlined",
                  "hide-details": "",
                  class: "adder-input",
                  "onUpdate:modelValue": onAdderPerMeterInput,
                  onKeyup: _withKeys(save, ["enter"])
                }, null, 8, ["model-value"]),
                _createVNode(_component_v_text_field, {
                  "model-value": adderPerFeet.value,
                  type: "text",
                  inputmode: "decimal",
                  label: "Adder per Feet",
                  prefix: "$",
                  suffix: "/F",
                  "suffix-class": 'text-grey-darken-1',
                  density: "compact",
                  variant: "outlined",
                  "hide-details": "",
                  class: "adder-input",
                  "onUpdate:modelValue": onAdderPerFeetInput,
                  onKeyup: _withKeys(save, ["enter"])
                }, null, 8, ["model-value"])
              ]),
              _createElementVNode("div", _hoisted_2, [
                (!hasLength.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_3, " Line length unavailable — can't compute a formula price. "))
                  : (computedPrice.value !== null)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _cache[3] || (_cache[3] = _createTextVNode(" = ")),
                        _createElementVNode("span", _hoisted_4, _toDisplayString(formatPrice(computedPrice.value)), 1),
                        _createElementVNode("span", _hoisted_5, "at " + _toDisplayString(formatLength(lengthValue.value)) + " " + _toDisplayString(unitLabel(lineUnit.value)), 1)
                      ], 64))
                    : (_openBlock(), _createElementBlock("span", _hoisted_6, " Enter base and adder (" + _toDisplayString(formatLength(lengthValue.value)) + " " + _toDisplayString(unitLabel(lineUnit.value)) + " line). ", 1))
              ])
            ], 64))
      ])
    ]),
    _: 1
  }, 8, ["model-value"]))
}
}

})